<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { authService } from '@/api/auth'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faUser,
  faEnvelope,
  faLock,
  faShieldHalved,
  faCamera,
  faSave,
  faCheckCircle,
  faCircleExclamation,
  faPhone,
  faLocationDot,
} from '@fortawesome/free-solid-svg-icons'
import { getErrorMessage } from '@/utils/errorHandling'

library.add(
  faUser,
  faEnvelope,
  faLock,
  faShieldHalved,
  faCamera,
  faSave,
  faCheckCircle,
  faCircleExclamation,
  faPhone,
  faLocationDot,
)

const authStore = useAuthStore()
const loading = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const form = ref({
  first_name: '',
  last_name: '',
  email: '',
  username: '',
  phone_number: '',
  location: '',
  password: '',
  confirmPassword: '',
})

onMounted(async () => {
  if (authStore.user) {
    form.value.first_name = authStore.user.first_name || ''
    form.value.last_name = authStore.user.last_name || ''
    form.value.email = authStore.user.email || ''
    form.value.username = authStore.user.username || ''
    form.value.phone_number = authStore.user.phone_number || ''
    form.value.location = authStore.user.location || ''
  } else {
    try {
      const user = await authService.getUserProfile()
      form.value.first_name = user.first_name
      form.value.last_name = user.last_name
      form.value.email = user.email
      form.value.username = user.username
      form.value.phone_number = user.phone_number || ''
      form.value.location = user.location || ''
    } catch (error) {
      console.error('Failed to load profile:', error)
    }
  }
})

const handleUpdateProfile = async () => {
  loading.value = true
  successMessage.value = ''
  errorMessage.value = ''

  if (form.value.password && form.value.password !== form.value.confirmPassword) {
    errorMessage.value = 'Passwords do not match'
    loading.value = false
    return
  }

  try {
    const updateData: any = {
      first_name: form.value.first_name,
      last_name: form.value.last_name,
      email: form.value.email,
      username: form.value.username,
      phone_number: form.value.phone_number,
      location: form.value.location,
    }

    if (form.value.password) {
      updateData.password = form.value.password
      updateData.confirmPassword = form.value.confirmPassword
    }

    await authService.updateProfile(updateData)
    await authStore.fetchUserProfile()
    successMessage.value = 'Profile updated successfully!'
    form.value.password = ''
    form.value.confirmPassword = ''
  } catch (error: any) {
    errorMessage.value = getErrorMessage(error, 'Failed to update profile')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="min-h-screen bg-slate-50/50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-4xl mx-auto">
      <!-- Header -->
      <div class="mb-10">
        <h1 class="text-3xl font-black text-slate-900 tracking-tight">Account Settings</h1>
        <p class="text-slate-500 font-medium">Manage your personal information and security preferences</p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Sidebar Info -->
        <div class="space-y-6">
          <div class="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm text-center">
            <div class="relative inline-block mb-4">
              <div class="w-24 h-24 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-3xl font-black border-4 border-white shadow-md">
                {{ form.first_name.charAt(0) }}{{ form.last_name.charAt(0) }}
              </div>
              <button class="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full shadow-md border border-slate-100 flex items-center justify-center text-slate-400 hover:text-indigo-600 transition-colors">
                <FontAwesomeIcon icon="camera" class="text-xs" />
              </button>
            </div>
            <h2 class="text-xl font-bold text-slate-800">{{ form.first_name }} {{ form.last_name }}</h2>
            <p class="text-sm text-slate-400 font-medium capitalize">{{ authStore.user?.role?.toLowerCase() }}</p>
            
            <div class="mt-6 pt-6 border-t border-slate-50">
              <div class="flex items-center gap-3 text-sm text-slate-500 mb-3">
                <FontAwesomeIcon icon="shield-halved" class="text-emerald-500" />
                <span>Account verified</span>
              </div>
              <div class="flex items-center gap-3 text-sm text-slate-500">
                <FontAwesomeIcon icon="envelope" class="opacity-40" />
                <span class="truncate">{{ form.email }}</span>
              </div>
            </div>
          </div>

          <div class="bg-indigo-600 p-6 rounded-3xl shadow-xl shadow-indigo-100 text-white">
            <h3 class="font-bold mb-2">Pro Tip</h3>
            <p class="text-sm text-indigo-100 leading-relaxed">
              Use a strong password with at least 8 characters, including numbers and special symbols to keep your account secure.
            </p>
          </div>
        </div>

        <!-- Main Form -->
        <div class="lg:col-span-2 space-y-8">
          <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
            <div class="p-8 border-b border-slate-50">
              <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
                <div class="w-1 h-5 bg-indigo-600 rounded-full"></div>
                Personal Information
              </h3>
            </div>

            <div class="p-8 space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 px-1">First Name</label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300">
                      <FontAwesomeIcon icon="user" />
                    </div>
                    <input 
                      v-model="form.first_name"
                      type="text" 
                      class="block w-full pl-11 pr-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 text-slate-700 font-bold transition-all"
                      placeholder="John"
                    />
                  </div>
                </div>
                <div>
                  <label class="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Last Name</label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300">
                      <FontAwesomeIcon icon="user" />
                    </div>
                    <input 
                      v-model="form.last_name"
                      type="text" 
                      class="block w-full pl-11 pr-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 text-slate-700 font-bold transition-all"
                      placeholder="Doe"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label class="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Email Address</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300">
                    <FontAwesomeIcon icon="envelope" />
                  </div>
                  <input 
                    v-model="form.email"
                    type="email" 
                    class="block w-full pl-11 pr-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 text-slate-700 font-bold transition-all"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div>
                <label class="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Username</label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300">
                    <div class="font-black">@</div>
                  </div>
                  <input 
                    v-model="form.username"
                    type="text" 
                    class="block w-full pl-11 pr-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 text-slate-700 font-bold transition-all"
                    placeholder="username"
                  />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Phone Number</label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300">
                      <FontAwesomeIcon icon="phone" />
                    </div>
                    <input 
                      v-model="form.phone_number"
                      type="text" 
                      class="block w-full pl-11 pr-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 text-slate-700 font-bold transition-all"
                      placeholder="e.g. 0712345678"
                    />
                  </div>
                </div>
                <div>
                  <label class="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Residential Area/Location</label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300">
                      <FontAwesomeIcon icon="location-dot" />
                    </div>
                    <input 
                      v-model="form.location"
                      type="text" 
                      class="block w-full pl-11 pr-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 text-slate-700 font-bold transition-all"
                      placeholder="e.g. Dar es Salaam"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
            <div class="p-8 border-b border-slate-50">
              <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
                <div class="w-1 h-5 bg-indigo-600 rounded-full"></div>
                Security
              </h3>
            </div>

            <div class="p-8 space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 px-1">New Password</label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300">
                      <FontAwesomeIcon icon="lock" />
                    </div>
                    <input 
                      v-model="form.password"
                      type="password" 
                      class="block w-full pl-11 pr-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 text-slate-700 font-bold transition-all"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
                <div>
                  <label class="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Confirm Password</label>
                  <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300">
                      <FontAwesomeIcon icon="lock" />
                    </div>
                    <input 
                      v-model="form.confirmPassword"
                      type="password" 
                      class="block w-full pl-11 pr-4 py-3 bg-slate-50 border-none rounded-2xl focus:ring-2 focus:ring-indigo-600 text-slate-700 font-bold transition-all"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>

              <!-- Notifications -->
              <div v-if="successMessage" class="p-4 bg-emerald-50 rounded-2xl border border-emerald-100 flex items-center gap-3 text-emerald-700 font-bold">
                <FontAwesomeIcon icon="check-circle" />
                {{ successMessage }}
              </div>

              <div v-if="errorMessage" class="p-4 bg-rose-50 rounded-2xl border border-rose-100 flex items-center gap-3 text-rose-700 font-bold">
                <FontAwesomeIcon icon="circle-exclamation" />
                {{ errorMessage }}
              </div>

              <div class="pt-4 flex justify-end">
                <button 
                  @click="handleUpdateProfile"
                  :disabled="loading"
                  class="px-8 py-3 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <FontAwesomeIcon v-if="loading" icon="circle-notch" class="animate-spin" />
                  <FontAwesomeIcon v-else icon="save" />
                  {{ loading ? 'Updating...' : 'Save Changes' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Add transition for standard fontawesome icon which is not added globally but imported here */
.fa-circle-notch {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
